import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addShareMembers, listShareMembers, removeShareMember, setMemberRole,
} from '../../api/share.ts';
import { formatDate } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import { Modal } from '../common/Modal.tsx';
import { IconPlus, IconTrash } from '../../lib/icons.tsx';
import type { SharedFolder } from '../../api/share.ts';

const ROLE_LABEL: Record<string, string> = {
  owner: '소유자', editor: '편집 가능', viewer: '보기 전용',
};

export function MembersDialog({ folder, onClose }: { folder: SharedFolder; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'editor' | 'viewer'>('editor');

  const { data, isLoading } = useQuery({
    queryKey: ['share-members', folder.namespace_id],
    queryFn: () => listShareMembers(folder.namespace_id),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['share-members', folder.namespace_id] });
    void queryClient.invalidateQueries({ queryKey: ['shared-folders'] });
  };

  const invite = useMutation({
    mutationFn: () => addShareMembers(folder.namespace_id, [{ email: email.trim(), role }]),
    onSuccess: () => { toast.success('초대했습니다'); setEmail(''); refresh(); },
    onError: (e: Error) => { toast.error(e); },
  });

  const changeRole = useMutation({
    mutationFn: (v: { userId: string; role: 'editor' | 'viewer' }) =>
      setMemberRole(folder.namespace_id, v.userId, v.role),
    onSuccess: () => { toast.success('권한을 바꿨습니다'); refresh(); },
    onError: (e: Error) => { toast.error(e); },
  });

  const remove = useMutation({
    mutationFn: (userId: string) => removeShareMember(folder.namespace_id, userId),
    onSuccess: () => { toast.info('참여자를 제거했습니다'); refresh(); },
    onError: (e: Error) => { toast.error(e); },
  });

  const members = data?.items ?? [];

  return (
    <Modal title={`'${folder.name}' 참여자`} onClose={onClose}>
      {folder.is_owner && (
        <div className="field">
          <label htmlFor="member-email">참여자 초대</label>
          <div className="row">
            <input
              id="member-email" className="input" type="email" placeholder="name@example.com"
              value={email} onChange={(e) => { setEmail(e.target.value); }}
            />
            <select
              className="input" style={{ width: 110 }} value={role} aria-label="권한"
              onChange={(e) => { setRole(e.target.value as 'editor' | 'viewer'); }}
            >
              <option value="editor">편집 가능</option>
              <option value="viewer">보기 전용</option>
            </select>
            <button
              className="btn btn--primary btn--icon" aria-label="초대"
              disabled={!email.trim() || invite.isPending}
              onClick={() => { invite.mutate(); }}
            >
              <IconPlus size={16} />
            </button>
          </div>
        </div>
      )}

      {isLoading && <span className="spinner" />}
      {members.map((member) => (
        <div key={member.user_id} className="row" style={{ marginBottom: 8 }}>
          <div className="spacer" style={{ minWidth: 0 }}>
            <div className="truncate">{member.name}</div>
            <div className="faint" style={{ fontSize: 12 }}>
              {member.email} · {formatDate(member.joined_at)} 참여
            </div>
          </div>
          {member.role === 'owner' || !folder.is_owner ? (
            <span className="frow__badge">{ROLE_LABEL[member.role]}</span>
          ) : (
            <>
              <select
                className="input" style={{ width: 120, height: 30 }}
                value={member.role} aria-label={`${member.name} 권한`}
                onChange={(e) => {
                  changeRole.mutate({
                    userId: member.user_id, role: e.target.value as 'editor' | 'viewer',
                  });
                }}
              >
                <option value="editor">편집 가능</option>
                <option value="viewer">보기 전용</option>
              </select>
              <button
                className="btn btn--sm btn--icon" aria-label={`${member.name} 제거`}
                onClick={() => { remove.mutate(member.user_id); }}
              >
                <IconTrash size={14} />
              </button>
            </>
          )}
        </div>
      ))}
    </Modal>
  );
}
